/*!
=========================================================
* Regiform IMS Frontend - Session Timeout Monitor
=========================================================
* Component that monitors session timeout and redirects
* to login when the session expires
=========================================================
*/

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useBottomNotificationStore } from '../../stores/bottomNotificationStore';

const SESSION_CHECK_INTERVAL = 60000; // Check every 60 seconds
const TOKEN_EXPIRY_MS = 7200000; // 2 hours in milliseconds (allows token auto-refresh to work)
const WARNING_BEFORE_EXPIRY_MS = 300000; // Show warning 5 minutes before expiry

function SessionTimeoutMonitor() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const showNotification = useBottomNotificationStore((s) => s.showNotification);
  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const checkIntervalRef = useRef(null);

  useEffect(() => {
    // Only monitor if user is logged in
    if (!user) {
      return;
    }

    // Reset last activity time on component mount
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;

    // Track user activity (mouse, keyboard, touch)
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      warningShownRef.current = false; // Reset warning if user is active
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Check for session timeout periodically
    const checkSessionTimeout = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;
      const timeUntilExpiry = TOKEN_EXPIRY_MS - timeSinceLastActivity;

      // Show warning before expiry
      if (timeUntilExpiry <= WARNING_BEFORE_EXPIRY_MS && timeUntilExpiry > 0 && !warningShownRef.current) {
        warningShownRef.current = true;
        const minutesRemaining = Math.ceil(timeUntilExpiry / 60000);
        showNotification(
          `Your session will expire in ${minutesRemaining} minute${minutesRemaining > 1 ? 's' : ''} due to inactivity`,
          "warning",
          10000,
        );
      }

      // Session has expired
      if (timeSinceLastActivity >= TOKEN_EXPIRY_MS) {
        showNotification('Your session has expired. Please login again.', "error");
        logout();
        navigate('/sign-in', { replace: true });
      }
    };

    // Start checking for timeout
    checkIntervalRef.current = setInterval(checkSessionTimeout, SESSION_CHECK_INTERVAL);

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [user, logout, navigate, showNotification]);

  // This component doesn't render anything
  return null;
}

export default SessionTimeoutMonitor;
